// Pure text helpers for the news editor's inline formatting. The stored format
// stays the server's markdown-lite (CITS-Server/src/services/news/blocks.js):
// **bold** and [label](https://link), never HTML. Everything here edits plain
// strings and returns the new text plus the selection to restore, so it can be
// unit-checked without a DOM.

/** Modifier key name for shortcut hints. */
export const MOD_KEY = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent) ? 'Cmd' : 'Ctrl'

export interface Edit { text: string; start: number; end: number }

/** Same pattern as the server's INLINE_RE and BlockRenderer's. */
export const INLINE_RE = /\[([^\]\n]{1,200})\]\(([^)\s]{1,500})\)|\*\*((?:[^*\n]|\*(?!\*))+?)\*\*/g
/** Anything tag-shaped, which the server rejects. */
export const TAG_RE = /<[a-zA-Z/!?]/

/** Mirrors the server's isHttpsUrl: absolute https, real host, no credentials. */
export function isHttpsUrl(value: string): boolean {
  if (!value || value.length > 500 || /\s/.test(value)) return false
  let u: URL
  try { u = new URL(value) } catch { return false }
  if (u.protocol !== 'https:') return false
  if (u.username || u.password) return false
  // The link syntax ends at ")", so a URL containing one would be cut short.
  if (value.includes(')')) return false
  return !!u.hostname && u.hostname.includes('.')
}

interface Token { kind: 'bold' | 'link'; start: number; end: number; label: string; href?: string }

function tokens(text: string): Token[] {
  const out: Token[] = []
  const re = new RegExp(INLINE_RE.source, 'g')
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) !== null) {
    if (m[1] !== undefined) out.push({ kind: 'link', start: m.index, end: m.index + m[0].length, label: m[1], href: m[2] })
    else out.push({ kind: 'bold', start: m.index, end: m.index + m[0].length, label: m[3] })
  }
  return out
}

/** The bold or link token containing the range [start, end], if any. */
function tokenAt(text: string, start: number, end: number, kind: Token['kind']): Token | undefined {
  return tokens(text).find((t) => t.kind === kind && (start === end
    ? start > t.start && start < t.end // a caret must sit strictly inside
    : start >= t.start && end <= t.end))
}

const PLACEHOLDER = 'bold text'

/**
 * Bold toggle. No selection: unwraps the bold span around the caret, or
 * inserts "**bold text**" with the words selected. A selection inside (or
 * exactly around) a bold span unwraps it; otherwise each line of the selection
 * is wrapped (bold can't cross a line break) after dropping stray "**" in it.
 */
export function toggleBold(text: string, start: number, end: number): Edit {
  if (start > end) [start, end] = [end, start]

  const around = tokenAt(text, start, end, 'bold')
  if (around) {
    const inner = around.label
    const next = text.slice(0, around.start) + inner + text.slice(around.end)
    const innerStart = around.start
    if (start === end) {
      const caret = Math.min(Math.max(start - 2, innerStart), innerStart + inner.length)
      return { text: next, start: caret, end: caret }
    }
    return { text: next, start: innerStart, end: innerStart + inner.length }
  }

  if (start === end) {
    const next = text.slice(0, start) + `**${PLACEHOLDER}**` + text.slice(end)
    return { text: next, start: start + 2, end: start + 2 + PLACEHOLDER.length }
  }

  // Leave surrounding spaces outside the markers ("**word** " not "**word **").
  let s = start
  let e = end
  while (s < e && /\s/.test(text[s])) s++
  while (e > s && /\s/.test(text[e - 1])) e--
  if (s === e) return { text, start, end }

  const selected = text.slice(s, e).replace(/\*\*/g, '')
  const wrapped = selected.split('\n').map((line) => {
    const lead = line.match(/^\s*/)?.[0] ?? ''
    const trail = line.match(/\s*$/)?.[0] ?? ''
    const core = line.slice(lead.length, line.length - trail.length)
    return core ? `${lead}**${core}**${trail}` : line
  }).join('\n')
  const next = text.slice(0, s) + wrapped + text.slice(e)
  return { text: next, start: s, end: s + wrapped.length }
}

/**
 * Mirrors the server's checkInline for what an author can type by hand:
 * null when fine, else a short fix-it message.
 */
export function inlineProblem(text: string): string | null {
  if (TAG_RE.test(text)) return 'HTML tags are not allowed. Use the Bold and Link buttons instead.'
  const links = tokens(text).filter((t) => t.kind === 'link')
  if (links.some((l) => !isHttpsUrl(l.href ?? ''))) return 'Links must be https:// addresses.'
  if ((text.match(/\]\(/g) || []).length !== links.length) return 'A link is not finished. Select the text and use Link to fix it.'
  return null
}

/** What the link popover should start with for this selection. */
export function linkContext(text: string, start: number, end: number): { label: string; url: string; editing: boolean } {
  if (start > end) [start, end] = [end, start]
  const link = tokenAt(text, start, end, 'link')
  if (link) return { label: link.label, url: link.href ?? '', editing: true }
  return { label: cleanLabel(text.slice(start, end)), url: '', editing: false }
}

/** A link label the server's pattern accepts: no "]", no line breaks, <= 200. */
export function cleanLabel(label: string): string {
  return label.replace(/[\]\n\r]/g, ' ').replace(/\[/g, '').replace(/\s+/g, ' ').trim().slice(0, 200)
}

/**
 * Inserts [label](url) over the selection, or replaces the link the
 * selection sits in. The label falls back to the URL's host.
 */
export function applyLink(text: string, start: number, end: number, label: string, url: string): Edit {
  if (start > end) [start, end] = [end, start]
  const link = tokenAt(text, start, end, 'link')
  let s = start
  let e = end
  if (link) { s = link.start; e = link.end } else {
    while (s < e && /\s/.test(text[s])) s++
    while (e > s && /\s/.test(text[e - 1])) e--
  }
  let host = url
  try { host = new URL(url).hostname } catch { /* keep url */ }
  const md = `[${cleanLabel(label) || host}](${url.trim()})`
  return { text: text.slice(0, s) + md + text.slice(e), start: s, end: s + md.length }
}

/** Removes the link the selection sits in, keeping its label. */
export function removeLink(text: string, start: number, end: number): Edit {
  if (start > end) [start, end] = [end, start]
  const link = tokenAt(text, start, end, 'link')
  if (!link) return { text, start, end }
  return { text: text.slice(0, link.start) + link.label + text.slice(link.end), start: link.start, end: link.start + link.label.length }
}

// ── Lists ───────────────────────────────────────────────────────────────────

/** "1. ", "2) ", "10.  " at the start of an item (not "1.5 hours"). */
const NUMBER_PREFIX = /^\s*\d{1,3}[.)]\s+/
/** "- " or "• " typed at the start of a bullet item. */
const BULLET_PREFIX = /^\s*[-•]\s+/

export const hasNumberPrefix = (item: string) => NUMBER_PREFIX.test(item)
export const hasBulletPrefix = (item: string) => BULLET_PREFIX.test(item)

/** Drops the number or bullet an author typed; the list draws its own. */
export function stripListMarker(item: string, ordered: boolean): string {
  return item.replace(ordered ? NUMBER_PREFIX : BULLET_PREFIX, '')
}

/** A bulleted list whose filled items all start with numbers. */
export function looksNumbered(items: string[]): boolean {
  const filled = items.filter((x) => x.trim())
  return filled.length > 0 && filled.every(hasNumberPrefix)
}

// ── Versions ────────────────────────────────────────────────────────────────

/** min_app_version as the server accepts it (itemInput.js). */
export const APP_VERSION_RE = /^\d+(\.\d+){0,3}$/
/** Release version as the server accepts it (blocks.js validateHeader). */
export const RELEASE_VERSION_RE = /^[0-9A-Za-z][0-9A-Za-z.\-+]{0,31}$/
